//codex-- Page-scoped toolbar layout; retain this project's handlers and data.
import PageToolbar, { ToolbarField } from './PageToolbar';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
//codex-- Keep search and Reset icons compact and accessible in the page-local controls.
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import DateFormat from '../components/DateFormat';
import LoadingSpinner from '../components/LoadingSpinner';
import HeadingCp from '../components/HeadingCp';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Truncate from '../components/Truncate';
import ModalWindow from '../components/ModalWindow';

const AuditLogReport = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [notesIdOpen, setNotesIdOpen] = useState(null);
  const [newValueIdOpen, setNewValueIdOpen] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/config_system_crud.php`;

  const [employees, setEmployees] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterEmployeeId, setFilterEmployeeId] = useState(''); // store employee id




  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        });
        setEmployees(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error fetching employees', e);
      }
    };

    fetchEmployees();
  }, [apiUrl]);


  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(api, { params: { getLogs: 1 } });
      const data = Array.isArray(res.data) ? res.data : [];
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      toast.error('Error fetching audit logs', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value === '') {
      setFilteredLogs(logs);
    } else {
      const filtered = logs.filter((log) =>
        (log.username || '').toLowerCase().includes(value.toLowerCase()) ||
        (log.action || '').toLowerCase().includes(value.toLowerCase()) ||
        (log.module_name || '').toLowerCase().includes(value.toLowerCase()) ||
        (log.description || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredLogs(filtered);
    }
  };

  const handleSearchClick = async () => {
    setLoading(true);
    try {
      const response = await axios.post(api, {
        status: 'searchLogs',
        data: { search: searchTerm }
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      toast.error('Search failed', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Please provide both start and end dates.", { position: "top-center" });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(api, {
        status: 'filterLogs',
        data: {
          date_from: dateFrom,
          date_to: dateTo,
          user_id: filterEmployeeId || null // optional
        }
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      toast.error('Filter failed', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotesView = (id) => {
    setNotesIdOpen(id);
  };
 

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
      <HeadingCp label="Audit Log" />

      <>
        {/* //codex-- Match the audit screenshot and keep the search/reset controls inside the same card. */}
        <PageToolbar title="Audit log tools">
          {/* //codex-- Keep From, To, Employee, and the original Filter action inline on desktop. */}
          <div className="flex w-full flex-wrap items-end gap-3">
            <ToolbarField label="From"><input type="date" className="border border-gray-300 rounded-md px-2 py-2" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></ToolbarField>
            <span className="hidden sm:block text-gray-600" aria-hidden="true">to</span>
            <ToolbarField label="To"><input type="date" className="border border-gray-300 rounded-md px-2 py-2" value={dateTo} onChange={e => setDateTo(e.target.value)} /></ToolbarField>
            <ToolbarField label="Employee"><select value={filterEmployeeId} onChange={e => setFilterEmployeeId(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2">
                <option value="">All Employees</option>
                {employees?.map(emp => <option key={emp.id} value={emp.id}>
                  {emp.first_name}
                  {emp.last_name}
                </option>)}
              </select></ToolbarField>
            <button type="button" onClick={handleDateFilter} className="bg-blue-500 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded">Filter</button>
          </div>
          {/* //codex-- Preserve live filtering and Enter search while keeping Reset beside the input. */}
          <div className="flex w-full flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1">
              <ToolbarField label="Search audit logs">
                <div className="page-toolbar-search-input">
                  <MagnifyingGlassIcon aria-hidden="true" className="h-4 w-4" />
                  <input
                    type="text"
                    name="search"
                    value={searchTerm}
                    required
                    onChange={handleSearchChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSearchClick();
                      }
                    }}
                    placeholder="Enter a search term"
                  />
                </div>
              </ToolbarField>
            </div>
            {/* //codex-- Retain the existing reload reset so search and all filter selections return to their defaults. */}
            <button type="button" onClick={() => window.location.reload()} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-md">
              <ArrowPathIcon aria-hidden="true" className="h-4 w-4" />
              Reset
            </button>
          </div>
        </PageToolbar>
      </>
      <br/>

      {loading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : (
        <table className="min-w-full divide-y divide-gray-300 bg-gray-200 border rounded-lg text-xs sm:text-sm">
          <thead>
            <tr className="divide-x divide-gray-400">
              <th className="py-3.5 px-3 text-left font-semibold text-gray-900">Date/Time</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">User</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Action</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Module</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Record ID</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Description</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Old Values</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">New Values</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log, index) => (
              <tr key={log.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'} divide-x`}>
                <td className="px-3 py-2 text-gray-800"><DateFormat date={log.event_time} /></td>
                <td className="px-3 py-2 text-gray-800">{log.username || log.user_id}</td>
                <td className="px-3 py-2 text-gray-800">{log.action}</td>
                <td className="px-3 py-2 text-gray-800">{log.module_name}</td>
                <td className="px-3 py-2 text-gray-800">{log.record_id}</td>
                <td className="px-3 py-2 text-gray-800">{log.description}</td>
                <td className="px-3 py-2 text-gray-600 break-all max-w-xs">
                  {/* {log.old_values} */}
                    {log.old_values && 
                      <Truncate text={log.old_values} handleNotesView={() => handleNotesView(log.id)} />
                    }
                    {notesIdOpen === log.id && (
                      <ModalWindow job={log.old_values} closeNotes={() => setNotesIdOpen(null)} activeButton={true} />
                    )}                  
                </td> 
                <td className="px-3 py-2 text-gray-600 break-all max-w-xs">
                  {/* {log.new_values} */}
                   {log.new_values && 
                      <Truncate text={log.new_values} handleNotesView={() => handleNotesView(log.id)} />
                    }
                    {newValueIdOpen === log.id && (
                      <ModalWindow job={log.new_values} closeNotes={() => setNewValueIdOpen(null)} activeButton={true} />
                    )}                   
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AuditLogReport;
